/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */

import { MappedTag, AccessLevel } from '@ouster/webviz-react-components';

import * as Sentry from '@sentry/react';
import {
  action,
  thunk,
  computed,
  Computed,
  Action as EzPzAction,
  Thunk as EzPzThunk,
} from 'easy-peasy';

// TODO (mw) Fix dependency cycle
// eslint-disable-next-line import/no-cycle

import {
  LoadStatus,
  Output,
  RawDriveInfo,
  DriveInfo,
  FileInfo,
  OutputFileInfo,
  PublicationInfo,
  RawPublicationResponse,
  RecordingAccessInfo,
} from '../types';
import { BASE_ORG_API, BASE_PUBLISHED_RECORDING_API } from '../utils/constants';
import { transformRawDrives } from '../utils/driveUtil';
import {
  fetchWithAuth,
  fetchWithAuthV2,
  getSignedUrl,
} from '../utils/requests';
import { updateFrameInUrl } from '../utils/searchParams';
import { fetchWithStatus } from '../utils/state';

/// ///////////////////////////////////////////////////////////////////////////
// Shared Types And Helpers
/// ///////////////////////////////////////////////////////////////////////////

export type EventType = 'note';
export type SingleOsfInfo = {
  version: number;
  numFrames: number;
  dataPath: string;
  sensors: string[];
  startIdx: number;
  firstTs?: string | number;
  lastTs?: string | number;
  modelName: string;
  modelPath: string;
  mode: string;
  units: string;
  sensor_model: string;
  lidarMode?: number;
  h: number;
  w: number;
  range_scale: number;
};

// TODO: Can we not duplicate it?
export type RawDriveOsfInfo = {
  config_by_id: {
    [key: string]: SingleOsfInfo;
  };
  offsets_by_id: {
    [key: string]: number;
  };
  ids: string[];
};
export type DriveOsfInfo = {
  configById: {
    [key: string]: SingleOsfInfo;
  };
  offsetsById: {
    [key: string]: number;
  };
  ids: string[];
};

// TODO (mw): Remove old car event types
// TODO: Put these functions and types elsewhere
export type CarEvent = {
  eventType: string;
  secs: number;
  nsecs: number;
};

export type Sensor = {
  extrinsics: Array<number>;
};

/**
 * Helper function to estimate the timestamp of a frame index
 * Note (mw) - this is currently unused. remove?
 * @param frame frame index
 * @param driveOsfInfo drive osf info
 * @returns estimated ts, 0 if unable to guess
 */
export function estimateTsFromFrame(
  frame: number,
  driveOsfInfo?: DriveOsfInfo
) {
  if (!driveOsfInfo) {
    return 0;
  }

  const { ids, configById, offsetsById } = driveOsfInfo;
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    const offset = offsetsById[id];
    const nextOffset = offsetsById[ids[i + 1]] || Infinity;
    if (frame < nextOffset) {
      // It's in this chunk.
      const numInChunk = frame - offset;
      let first = configById[id].firstTs;
      if (!first) {
        // Some older OSFs won't have time ranges
        return 0;
      }
      first = +first;
      first /= 1000;

      return first + 100 * numInChunk;
    }
  }
  // TODO: Better default case?
  return 0;
}

/**
 * Estimate the frame index from a timestamp (ms)
 * Returns zero otherwise
 * @param tsInMs timestamp
 * @param driveOsfInfo drive OSF info
 * @returns estimated frame index
 */
export function estimateFrameFromTs(
  tsInMs: number,
  driveOsfInfo: DriveOsfInfo
) {
  const { ids, configById, offsetsById } = driveOsfInfo;
  // First find the bag that the frame is in.
  for (let idx = 0; idx < ids.length; idx++) {
    const id = ids[idx];
    const config = configById[id];
    const offset = offsetsById[id];
    if (!config.firstTs || !config.lastTs) {
      return 0;
    }

    let first = +config.firstTs;
    first /= 1000;
    let last = +config.lastTs;
    last /= 1000;
    if (tsInMs >= first && tsInMs < last) {
      const numFramesIn = Math.round((tsInMs - first) / 100); // 10Hz from milliseconds
      const targetFrame = offset + numFramesIn;
      return targetFrame;
    }
  }
  return 0;
}

/**
 * Get section ID from global frame index
 * @param frameNum frame index in context of entire recording
 * @param driveOsfInfo drive osf info
 * @returns section ID, and frame index
 */
export function getSectionIdFromGlobalFrame(
  frameNum: number,
  driveOsfInfo: DriveOsfInfo
): [string, number] {
  const { ids, configById, offsetsById } = driveOsfInfo;
  let idx = 0;
  let num = frameNum;
  for (let i = 0; i < ids.length - 1; i++) {
    if (frameNum < offsetsById[ids[i + 1]]) {
      break;
    }
    idx += 1;
    num -= configById[ids[i]].numFrames;
  }

  const finalSectionId = ids[idx];
  return [finalSectionId, num];
}

export const publicationUuidToURL = (uuid: string) =>
  `${window.location.origin}/share/${uuid}`;

const transformRawPublicationResponse = (raw: RawPublicationResponse) => {
  const publicationInfo = {
    linkUrl: publicationUuidToURL(raw.uuid),
    publishedAt: new Date(`${raw.published_at}Z`),
    publishedBy: raw.published_by_name,
    updatedAt: new Date(`${raw.updated_at}Z`),
    updatedBy: raw.updated_by_name,
    title: raw.title,
    isSample: raw.is_sample,
    publishedById: parseInt(raw.published_by_id, 10),
    uuid: raw.uuid,
  };
  return publicationInfo;
};

/// ///////////////////////////////////////////////////////////////////////////
// State Model
/// ///////////////////////////////////////////////////////////////////////////

export type State = {
  loadStatus: LoadStatus;
  driveId: number;
  currentFrameIdx: number;
  lastReceivedFrameIdx: number | undefined;
  seeking: boolean;
  numFrames: number;
  frameStartIdx: number;
  frameEndIdx: number;
  rate: number;
  sensorOffsets: number[];
  osfParams: {
    width: number;
    height: number;
    sensors: string[];
  };
  playing: boolean;
  playbackSpeed: number;
  playbackSpeedOptions: number[];
  driveOsfInfo?: DriveOsfInfo;
  driveInfo?: DriveInfo; // Supercede with the sensor metadata in viz scene store?
  carEvents: CarEvent[];
  updateTagStatus: LoadStatus;
  activeEventType: EventType;

  // Download files
  sourceFileInfo: FileInfo[];
  outputFileInfo: OutputFileInfo[];
  // This is to mark when all all files to download are all collected. This
  // means that all outputs are finished and files are ready to download. No new
  // files can be added and no point in polling ever again
  filesToDownloadReady: boolean;

  // Publication
  isPublished: boolean;
  publicationInfo?: PublicationInfo;
  publicationLoadStatus: LoadStatus;
  isSample: boolean;
  isShared: boolean;

  // Private shared from ouster to other organizations
  privateSharedToOrgs: number[];

  // TODO(pierre) on 2021-04-28: Move to a separate store
  mappedTags?: MappedTag[];

  // status for new content indicator
  newContentStatus: {
    PUBLIC_PRIVATE: boolean;
    SPECIFIC_ORGS: boolean;
  };
};

export const defaultState: State = {
  loadStatus: 'notStarted',
  driveId: -1,
  currentFrameIdx: 0,
  lastReceivedFrameIdx: undefined,
  seeking: false,
  numFrames: Infinity,
  frameStartIdx: 0,
  frameEndIdx: Infinity,
  rate: 1,
  sensorOffsets: [0, 0, 0], // Can only be positive? TODO: Determine this
  osfParams: {
    width: 1024,
    height: 64,
    sensors: ['sensor1', 'sensor2', 'sensor3'],
  },
  playing: false,
  playbackSpeed: 1,
  playbackSpeedOptions: [0.25, 0.5, 1, 2],
  carEvents: [],
  updateTagStatus: 'notStarted',
  activeEventType: 'note',
  sourceFileInfo: [],
  outputFileInfo: [],
  filesToDownloadReady: false,
  isPublished: false,
  publicationLoadStatus: 'notStarted',
  isSample: false,
  isShared: false,
  privateSharedToOrgs: [],
  newContentStatus: {
    PUBLIC_PRIVATE: false,
    SPECIFIC_ORGS: false,
  },
};

type DriveData = {
  driveInfo: DriveInfo;
  driveOsfInfo?: DriveOsfInfo;
};

export interface DriveStateModel extends State {
  // Actions & Thunks
  loadDriveById: EzPzThunk<
    DriveStateModel,
    { id: string; isPrivate: boolean; orgId?: number }
  >;
  getOrgRecordingNum: EzPzThunk<DriveStateModel, { orgId: number }>;
  refreshDriveById: EzPzThunk<
    DriveStateModel,
    { id: string; isPrivate: boolean; orgId?: number }
  >;
  setFrameFromTs: EzPzThunk<DriveStateModel, number>;
  setNumFrames: EzPzAction<DriveStateModel, number>;
  setDriveInfo: EzPzAction<DriveStateModel, DriveInfo>;
  updateTags: EzPzThunk<
    DriveStateModel,
    { newTags: MappedTag[]; orgId: number }
  >;
  setLoadStatus: EzPzAction<DriveStateModel, LoadStatus>;
  setNewContentStatus: EzPzAction<
    DriveStateModel,
    {
      PUBLIC_PRIVATE: boolean;
      SPECIFIC_ORGS: boolean;
    }
  >;
  loadDataForDrive: EzPzAction<DriveStateModel, DriveData>;
  setActiveEventType: EzPzAction<DriveStateModel, EventType>;
  setCurrentFrameIdx: EzPzAction<DriveStateModel, number>;
  setlastReceivedFrameIdx: EzPzAction<DriveStateModel, number>;
  setSeeking: EzPzAction<DriveStateModel, boolean>;
  setPlaying: EzPzAction<DriveStateModel, boolean>;
  setPlaybackSpeed: EzPzAction<DriveStateModel, number>;
  increasePlaybackSpeed: EzPzThunk<DriveStateModel>;
  decreasePlaybackSpeed: EzPzThunk<DriveStateModel>;
  togglePause: EzPzThunk<DriveStateModel>;
  addTag: EzPzAction<DriveStateModel, string>;
  removeTag: EzPzAction<DriveStateModel, string>;
  setTagStatus: EzPzAction<DriveStateModel, LoadStatus>;
  setMappedTags: EzPzAction<DriveStateModel, MappedTag[]>;

  // Download
  setSourceFileInfo: EzPzAction<DriveStateModel, FileInfo[]>;
  loadSourceFileInfo: EzPzThunk<
    DriveStateModel,
    { id: string; isPrivate: boolean; orgId?: number }
  >;
  setOutputFileInfo: EzPzAction<DriveStateModel, OutputFileInfo[]>;
  loadOutputFileInfo: EzPzThunk<
    DriveStateModel,
    { id: string; isPrivate: boolean; orgId?: number }
  >;
  markDownloadFilesReady: EzPzAction<DriveStateModel>;

  // Publication
  loadPublicationInfo: EzPzThunk<
    DriveStateModel,
    { id: number; orgId: number }
  >;
  publishDrive: EzPzThunk<
    DriveStateModel,
    { title: string; orgId: number; driveId: number },
    any,
    any,
    Promise<PublicationInfo>
  >;
  updateSampleStatus: EzPzThunk<
    DriveStateModel,
    { orgId: number; sampleStatus: boolean }
  >;
  updateDrivePublicTitle: EzPzThunk<
    DriveStateModel,
    { title: string; orgId: number }
  >;
  updateRecordingTitle: EzPzThunk<
    DriveStateModel,
    { title: string; orgId: number; driveId: number }
  >;
  loadShareInfo: EzPzThunk<DriveStateModel, { orgId: number; driveId: number }>;
  unpublishDrive: EzPzThunk<DriveStateModel, number>;
  unpublishDriveById: EzPzThunk<
    DriveStateModel,
    { orgId: number; driveId: number }
  >;
  setPublicationInfo: EzPzAction<DriveStateModel, PublicationInfo>;
  setIsPublished: EzPzAction<DriveStateModel, boolean>;
  setIsSample: EzPzAction<DriveStateModel, boolean>;
  setIsShared: EzPzAction<DriveStateModel, boolean>;
  setPublicationLoadStatus: EzPzAction<DriveStateModel, LoadStatus>;
  // DELETE DRIVE
  deleteDrive: EzPzThunk<
    DriveStateModel,
    { orgId: number; driveId: number | string }
  >;
  // REPROCESS DRIVE
  reprocessDrive: EzPzThunk<
    DriveStateModel,
    { orgId: number; driveId: number | string }
  >;
  // REPLICATE DRIVE
  replicateDrive: EzPzThunk<
    DriveStateModel,
    { orgId: number; driveId: number | string; targetRegion: string }
  >;
  // SHARE DRIVE WITH OUSTER
  shareWithOuster: EzPzThunk<
    DriveStateModel,
    {
      organizationId: number;
      driveId: number | string;
      shareWithOuster: boolean;
      notes?: string;
    }
  >;
  // SHARE THE DRIVE FROM OUSTER TO OTHER ORGANIZATIONS
  setPrivateSharedToOrgs: EzPzAction<DriveStateModel, number[]>;
  loadPrivateSharedToOrgs: EzPzThunk<
    DriveStateModel,
    { orgId: number; driveId: number },
    any,
    any,
    Promise<boolean>
  >;
  savePrivateSharedToOrgs: EzPzThunk<
    DriveStateModel,
    {
      organizationId: number;
      driveId: number | string;
      sharedToOrgs: number[];
    }
  >;
  deletePrivateSharedToOrgs: EzPzThunk<
    DriveStateModel,
    {
      organizationId: number;
      driveId: number | string;
    }
  >;
  loadAccessLevel: EzPzThunk<
    DriveStateModel,
    { organizationId: number; driveUuid: string },
    any,
    any,
    Promise<RecordingAccessInfo>
  >;
  updateAccessLevel: EzPzThunk<
    DriveStateModel,
    {
      organizationId: number;
      driveUuid: string;
      accessLevel: AccessLevel;
      title?: string;
      thumbnailUrl?: string;
    },
    any,
    any,
    Promise<void>
  >;
  loadNewContentStatus: EzPzThunk<DriveStateModel, { organizationId: number }>;
  updateNewContentStatus: EzPzThunk<
    DriveStateModel,
    { organizationId: number; viewType: 'PUBLIC_PRIVATE' | 'SPECIFIC_ORGS' }
  >;

  // COMPUTED STATE
  outputForDownload: Computed<DriveStateModel, OutputFileInfo[]>;
  outputForDownloadNonSlam: Computed<DriveStateModel, OutputFileInfo[]>;
  sourceDataForDownload: Computed<DriveStateModel, FileInfo[]>;
  sourceConfigForDownload: Computed<DriveStateModel, FileInfo | undefined>;

  outputProcesses: Computed<DriveStateModel, Output[]>;
}

// TODO: Linter to prevent destructuring on immer proxies?
export const model: DriveStateModel = {
  ...defaultState,

  // Actions & Thunks
  loadDriveById: thunk(async (actions, { id, isPrivate, orgId }) => {
    if (isPrivate && Number.isNaN(parseInt(id, 10))) {
      throw new Error(
        `You can only load a private drive with an integer id. Instead received id ${id}`
      );
    }

    return fetchWithStatus(
      actions.setLoadStatus,
      async (rawDrive: RawDriveInfo) => {
        const data = transformRawDrives(rawDrive);
        let driveOsfInfo: DriveOsfInfo | undefined;

        if (data.osfGenerationStatus === 'FINISHED') {
          const osfInfoSignedUrl = await getSignedUrl(
            'osf_info.json',
            id,
            orgId
          );
          const osfResp = await fetch(osfInfoSignedUrl);
          if (!osfResp.ok) {
            // TODO: Should we break more gracefully here?
            throw new Error('Bad OSF Response');
          }
          const rawOsfInfo = (await osfResp.json()) as RawDriveOsfInfo;
          driveOsfInfo = {
            ids: rawOsfInfo.ids,
            configById: rawOsfInfo.config_by_id,
            offsetsById: rawOsfInfo.offsets_by_id,
          };
        } else {
          console.warn(`osfGenerationStatus is not finished`);
        }

        actions.loadDataForDrive({ driveInfo: data, driveOsfInfo });

        if (isPrivate && orgId) {
          const idAsInt = parseInt(id, 10);
          await actions.loadPublicationInfo({ id: idAsInt, orgId });
          await actions.loadShareInfo({ orgId, driveId: idAsInt });
        }
      },
      isPrivate
        ? `${BASE_ORG_API}/${orgId}/recordings/${id}`
        : `${BASE_PUBLISHED_RECORDING_API}/${id}`
    );
  }),
  getOrgRecordingNum: thunk(async (actions, { orgId }) => {
    const endpoint = `${BASE_ORG_API}/${orgId}/recordings/count`;
    try {
      const recordingNum = (await fetchWithAuthV2(endpoint, {
        method: 'GET',
        fallbackErrorMessage: 'Failed to get recording number',
      })) as number;
      return recordingNum;
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          section: 'DriveStateModel',
          thunk: 'getOrgRecordingNum',
        },
        extra: { orgId },
      });
      throw new Error(`Failed to fetch org recording number: ${err}`);
    }
  }),

  /**
   * Reload only the drive itself
   *
   * There is another method loadDriveByID that does additional stuff
   */
  refreshDriveById: thunk(async (actions, { id, isPrivate, orgId }) => {
    if (isPrivate && Number.isNaN(parseInt(id, 10))) {
      throw new Error(
        `You can only load a private drive with an integer id. Instead received id ${id}`
      );
    }

    const url = isPrivate
      ? `${BASE_ORG_API}/${orgId}/recordings/${id}`
      : `${BASE_PUBLISHED_RECORDING_API}/${id}`;

    try {
      const resp = await fetchWithAuth(url, {
        method: 'GET',
      });
      if (!resp.ok) {
        throw new Error('Bad Response');
      }
      const json = (await resp.json()) as RawDriveInfo;
      const data = transformRawDrives(json);
      actions.setDriveInfo(data);
    } catch (err) {
      console.error('Error in refreshDriveById:', err);
      Sentry.captureException(err, {
        tags: {
          section: 'DriveStateModel',
          thunk: 'refreshDriveById',
        },
        extra: { id, isPrivate, orgId },
      });
    }
  }),

  setFrameFromTs: thunk((actions, tsInMs, { getState }) => {
    const state = getState();
    const { driveOsfInfo } = state;
    if (!driveOsfInfo) {
      console.warn('Attempted to setFrameFromTs without valid driveOsfInfo');
      return;
    }

    const targetFrame = estimateFrameFromTs(tsInMs, driveOsfInfo);
    if (targetFrame !== undefined) {
      // Instead of changing currentFrameIdx directly, we update the
      // URL, which will eventually lead the DriveViewer component to
      // call setCurrentFrameIdx. Making the URL the 'source of truth'
      // simplifies things here and lets the user change the frame
      // directly by updating the URL
      console.log('setFrameFromTs');
      updateFrameInUrl(targetFrame);
    }
  }),

  /**
   * Update drive tags Don't use fetchWithStatus response callbacks when making
   * call to tags endpoint since the response is 204
   * // TODO(pierre) on 2021-04-28: Switch to PUTting the full array of
   * // tag ids when the API supports it
   * A little strange since the driveInfo only maintains tags by name, whereas
   * the APIs use tag IDs. We use mappedTags to correlate the two.
   */
  updateTags: thunk((actions, { newTags, orgId }, { getState }) => {
    const { driveId, driveInfo, mappedTags } = getState();

    if (!driveInfo || !mappedTags) {
      console.warn(
        `Not updating tags, missing drive info or tag to id mapping`
      );
      return;
    }

    const oldTags = driveInfo.tags;

    // eslint-disable-next-line no-restricted-syntax
    for (const newTag of newTags) {
      if (!oldTags.includes(newTag.value)) {
        actions.addTag(newTag.value);
        fetchWithStatus(
          actions.setTagStatus,
          // Don't use the callback here since the response is 204
          () => {},
          `${BASE_ORG_API}/${orgId}/recordings/${driveId}/tags/${newTag.data.id}`,
          {
            method: 'PUT',
          }
        );
      }
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const tagName of oldTags) {
      if (newTags.filter((tag) => tag.label === tagName).length === 0) {
        // get corresponding tag object
        const tagToRemove = mappedTags.find((tag) => tag.value === tagName);
        if (!tagToRemove) {
          return;
        }
        actions.removeTag(tagName);
        fetchWithStatus(
          actions.setTagStatus,
          // Don't use the callback here since the response is 204
          () => {},
          `${BASE_ORG_API}/${orgId}/recordings/${driveId}/tags/${tagToRemove.data.id}`,
          {
            method: 'DELETE',
          }
        );
      }
    }
  }),

  setCurrentFrameIdx: action((state, idx: number) => {
    const min = 0;
    const max = state.numFrames - 1;
    const clampedIdx = Math.min(max, Math.max(min, idx));

    // If the frame is not valid and we need to clamp it, update the URL
    // first so that it stays the 'source of truth'. This will
    // eventually lead the DriveViewer component to call this
    // setCurrentFrameIdx function one more time with the correct frame
    if (clampedIdx !== idx) {
      updateFrameInUrl(clampedIdx);
    } else {
      state.currentFrameIdx = clampedIdx;
    }
  }),

  setlastReceivedFrameIdx: action((state, idx: number) => {
    state.lastReceivedFrameIdx = idx;
  }),

  setSeeking: action((state, seeking) => {
    state.seeking = seeking;
  }),

  togglePause: thunk((actions, _, { getState }) => {
    const { playing } = getState();
    actions.setPlaying(!playing);
  }),

  setPlaying: action((state, playing) => {
    state.playing = playing;
  }),

  setPlaybackSpeed: action((state, newPlaybackSpeed) => {
    state.playbackSpeed = newPlaybackSpeed;
  }),

  increasePlaybackSpeed: thunk((actions, _, { getState }) => {
    const { playbackSpeed, playbackSpeedOptions } = getState();
    const currentIndex = playbackSpeedOptions.indexOf(playbackSpeed);

    if (currentIndex < playbackSpeedOptions.length - 1) {
      actions.setPlaybackSpeed(playbackSpeedOptions[currentIndex + 1]);
    }
  }),

  decreasePlaybackSpeed: thunk((actions, _, { getState }) => {
    const { playbackSpeed, playbackSpeedOptions } = getState();
    const currentIndex = playbackSpeedOptions.indexOf(playbackSpeed);

    if (currentIndex > 0) {
      actions.setPlaybackSpeed(playbackSpeedOptions[currentIndex - 1]);
    }
  }),

  addTag: action((state, newTag) => {
    if (!state.driveInfo) {
      return;
    }

    if (!state.driveInfo.tags.includes(newTag)) {
      state.driveInfo.tags.push(newTag);
    }
  }),

  removeTag: action((state, newTag) => {
    if (!state.driveInfo) {
      return;
    }

    if (state.driveInfo.tags.includes(newTag)) {
      state.driveInfo.tags = state.driveInfo.tags.filter((t) => t !== newTag);
    }
  }),

  setTagStatus: action((state, status) => {
    state.updateTagStatus = status;
  }),

  setLoadStatus: action((state, status) => {
    // Ming (09/11/24) - Removed this because it was interrupting the drive load
    // and removing the frame index set from the URL.
    // Should not affect anything, but keeping as a comment just in case.
    // if (status === 'loading') {
    //   resetImmerState(state, defaultState);
    // }
    state.loadStatus = status;
  }),

  setDriveInfo: action((state, driveInfo: DriveInfo) => {
    state.driveInfo = driveInfo;
  }),

  loadDataForDrive: action((state, data) => {
    const { driveInfo, driveOsfInfo } = data;

    // parseInt doesn't seem to make typescript happy about the type
    // conversion.
    const driveId = +driveInfo.id;

    state.driveInfo = driveInfo;
    state.driveId = driveId;

    if (!driveOsfInfo) {
      return;
    }

    const { ids, configById } = driveOsfInfo;

    if (ids.length === 0) {
      throw new Error('Drive has no OSF bags.');
    }

    // TODO: Should any of these be in selectors instead?
    const numFrames = ids
      .map((id) => configById[id])
      .map((config) => config.numFrames)
      .reduce((acc, x) => acc + x, 0);

    const firstConfig = configById[ids[0]];

    state.driveOsfInfo = driveOsfInfo;
    state.numFrames = numFrames;
    state.frameStartIdx = 0;
    state.frameEndIdx = numFrames - 1;
    state.osfParams = {
      // For legacy reasons we can get it from lidarMode
      // But newer workers will produce w
      // So that is what we will read here
      width: firstConfig.lidarMode || firstConfig.w,
      height: firstConfig.h,
      sensors: firstConfig.sensors,
    };
  }),

  setActiveEventType: action((state, eventType) => {
    state.activeEventType = eventType;
  }),

  setMappedTags: action((state, mapping) => {
    state.mappedTags = mapping;
  }),

  setSourceFileInfo: action((state, files) => {
    state.sourceFileInfo = files;
  }),

  setOutputFileInfo: action((state, files) => {
    state.outputFileInfo = files;
  }),

  setPrivateSharedToOrgs: action((state, orgs) => {
    state.privateSharedToOrgs = orgs;
  }),

  /// ///////////////////////////////////////////////////////////////////////////
  // Publication
  /// ///////////////////////////////////////////////////////////////////////////
  loadPublicationInfo: thunk(async (actions, { id, orgId }, { getState }) => {
    await fetchWithStatus(
      actions.setPublicationLoadStatus,
      // eslint-disable-next-line @typescript-eslint/require-await
      async (raw: RawPublicationResponse) => {
        const publicationInfo = transformRawPublicationResponse(raw);
        actions.setIsPublished(true);
        actions.setPublicationInfo(publicationInfo);
        actions.setIsSample(publicationInfo.isSample);
      },
      `${BASE_ORG_API}/${orgId}/recordings/${id}/publication`
    );

    // When we get a 404, then the drive is not published yet, so we set
    // isPublished to false
    const { publicationLoadStatus } = getState();
    if (publicationLoadStatus === 'failed') {
      actions.setIsPublished(false);
    }
  }),

  loadShareInfo: thunk(async (actions, { orgId, driveId }) => {
    const endpoint = `${BASE_ORG_API}/${orgId}/shared_drives/${driveId}`;
    try {
      await fetchWithAuthV2(endpoint, {
        method: 'GET',
        fallbackErrorMessage: 'Failed to get share info',
      });

      actions.setIsShared(true);
    } catch (err) {
      actions.setIsShared(false);
    }
  }),

  publishDrive: thunk(async (actions, { title, orgId, driveId }) => {
    const endpoint = `${BASE_ORG_API}/${orgId}/recordings/${driveId}/publication`;

    try {
      const resp = (await fetchWithAuthV2(endpoint, {
        method: 'POST',
        fallbackErrorMessage: 'Failed to publish the recording',
        body: JSON.stringify({ title }),
      })) as RawPublicationResponse;
      const publicationInfo = transformRawPublicationResponse(resp);
      actions.setPublicationInfo(publicationInfo);

      actions.setIsPublished(true);

      return publicationInfo;
    } catch (err) {
      actions.setIsPublished(false);
      Sentry.captureException(err, {
        tags: {
          section: 'DriveStateModel',
          thunk: 'publishDrive',
        },
        extra: { title, orgId, driveId },
      });
      throw err;
    }
  }),

  deleteDrive: thunk(async (actions, { orgId, driveId }) => {
    const endpoint = `${BASE_ORG_API}/${orgId}/recordings/${driveId}`;

    try {
      await fetchWithAuthV2(endpoint, {
        method: 'DELETE',
        fallbackErrorMessage: 'Failed to delete the recording',
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          section: 'DriveStateModel',
          thunk: 'deleteDrive',
        },
        extra: { orgId, driveId },
      });
      throw err;
    }
  }),

  reprocessDrive: thunk(async (actions, { orgId, driveId }) => {
    const endpoint = `${BASE_ORG_API}/${orgId}/recordings/${driveId}/reprocess/`;

    try {
      (await fetchWithAuthV2(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          refresh_token: localStorage.getItem('kcRefreshToken'),
        }),
        fallbackErrorMessage: 'Failed to reprocess the recording',
      })) as unknown;
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'DriveStateModel', thunk: 'reprocessDrive' },
        extra: { orgId, driveId },
      });
      throw err;
    }
  }),

  replicateDrive: thunk(async (_, { orgId, driveId, targetRegion }) => {
    const endpoint = `${BASE_ORG_API}/${orgId}/recordings/${driveId}/replicate`;

    try {
      await fetchWithAuthV2(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          target_region: targetRegion,
        }),
        fallbackErrorMessage: 'Failed to replicate the recording',
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'DriveStateModel', thunk: 'replicateDrive' },
        extra: { orgId, driveId, targetRegion },
      });
      throw err;
    }
  }),

  shareWithOuster: thunk(
    async (actions, { organizationId, driveId, shareWithOuster, notes }) => {
      const endpoint = `${BASE_ORG_API}/${organizationId}/shared_drives/${driveId}/share_with_ouster`;

      try {
        await fetchWithAuthV2(endpoint, {
          method: 'PATCH',
          fallbackErrorMessage: 'Failed to share the recording with Ouster',
          body: JSON.stringify({
            share_with_ouster: shareWithOuster,
            ...(notes ? { notes } : {}),
          }),
        });
        actions.setIsShared(shareWithOuster);
      } catch (err) {
        Sentry.captureException(err, {
          tags: { section: 'DriveStateModel', thunk: 'shareWithOuster' },
          extra: { organizationId, driveId, shareWithOuster, notes },
        });
        throw err;
      }
    }
  ),

  setNewContentStatus: action((state, newContentStatus) => {
    state.newContentStatus = newContentStatus;
  }),

  loadNewContentStatus: thunk(async (actions, { organizationId }) => {
    const endpoint = `${BASE_ORG_API}/${organizationId}/shared_drives/notification/new_content`;
    try {
      const resp = (await fetchWithAuthV2(endpoint, {
        method: 'GET',
        fallbackErrorMessage: `Failed to load new content status for organization`,
      })) as {
        PUBLIC_PRIVATE: boolean;
        SPECIFIC_ORGS: boolean;
      };

      actions.setNewContentStatus(resp);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'DriveStateModel', thunk: 'loadAccessLevel' },
        extra: { organizationId },
      });
      throw err;
    }
  }),
  updateNewContentStatus: thunk(async (_, { organizationId, viewType }) => {
    const endpoint = `${BASE_ORG_API}/${organizationId}/shared_drives/notification/viewed`;
    try {
      await fetchWithAuthV2(endpoint, {
        method: 'PATCH',
        fallbackErrorMessage: `Failed to update new content status for organization`,
        body: JSON.stringify({
          view_type: viewType,
        }),
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'DriveStateModel', thunk: 'loadAccessLevel' },
        extra: { organizationId, viewType },
      });
      throw err;
    }
  }),

  loadPrivateSharedToOrgs: thunk(async (actions, { orgId, driveId }) => {
    const endpoint = `${BASE_ORG_API}/${orgId}/shared_drives/shared/${driveId}`;
    try {
      const resp = (await fetchWithAuthV2(endpoint, {
        method: 'GET',
        fallbackErrorMessage: 'Failed to get shared to orgs',
      })) as { name: string; organization_id: number }[];
      const orgIds = resp.map((org) => org.organization_id);
      actions.setPrivateSharedToOrgs(orgIds);
      return orgIds.length > 0;
    } catch (err) {
      actions.setPrivateSharedToOrgs([]);
      Sentry.captureException(err, {
        tags: { section: 'DriveStateModel', thunk: 'loadPrivateSharedToOrgs' },
        extra: { orgId, driveId },
      });
      throw err;
    }
  }),

  loadAccessLevel: thunk(async (_, { organizationId, driveUuid }) => {
    const endpoint = `${BASE_ORG_API}/${organizationId}/shared_drives/${driveUuid}/access_level`;
    try {
      const resp = (await fetchWithAuthV2(endpoint, {
        method: 'GET',
        fallbackErrorMessage: `Failed to get drive's access level`,
      })) as RecordingAccessInfo;

      return resp;
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'DriveStateModel', thunk: 'loadAccessLevel' },
        extra: { driveUuid },
      });
      throw err;
    }
  }),

  updateAccessLevel: thunk(
    async (
      actions,
      { organizationId, driveUuid, accessLevel, thumbnailUrl, title }
    ) => {
      const endpoint = `${BASE_ORG_API}/${organizationId}/shared_drives/${driveUuid}/access_level`;
      // `/api/v1/organizations/{org_id}/shared_drives/{pub_uuid}/access_level`;
      try {
        await fetchWithAuthV2(endpoint, {
          method: 'PATCH',
          fallbackErrorMessage: 'Failed to share the recording with Ouster',
          body: JSON.stringify({
            access_level: accessLevel,
            thumbnail: thumbnailUrl,
            title,
          }),
        });

        actions.loadNewContentStatus({ organizationId });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { section: 'DriveStateModel', thunk: 'updateAccessLevel' },
          extra: { driveUuid, accessLevel, thumbnailUrl, title },
        });
        throw err;
      }
    }
  ),

  savePrivateSharedToOrgs: thunk(
    async (actions, { organizationId, driveId, sharedToOrgs }) => {
      const endpoint = `${BASE_ORG_API}/${organizationId}/shared_drives/${driveId}/share/`;

      try {
        await fetchWithAuthV2(endpoint, {
          method: 'PATCH',
          fallbackErrorMessage: 'Failed to share the recording with Ouster',
          body: JSON.stringify({
            organization_ids: sharedToOrgs,
          }),
        });

        actions.loadNewContentStatus({ organizationId });
      } catch (err) {
        Sentry.captureException(err, {
          tags: {
            section: 'DriveStateModel',
            thunk: 'savePrivateSharedToOrgs',
          },
          extra: { organizationId, driveId, sharedToOrgs },
        });
        throw err;
      }
    }
  ),

  deletePrivateSharedToOrgs: thunk(async (_, { organizationId, driveId }) => {
    const endpoint = `${BASE_ORG_API}/${organizationId}/shared_drives/${driveId}/share/`;

    try {
      await fetchWithAuthV2(endpoint, {
        method: 'DELETE',
        fallbackErrorMessage:
          'Failed to delete shared the recording with Ouster',
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          section: 'DriveStateModel',
          thunk: 'deletePrivateSharedToOrgs',
        },
        extra: { organizationId, driveId },
      });
      throw err;
    }
  }),

  updateSampleStatus: thunk(
    async (actions, { orgId, sampleStatus }, { getState }) => {
      const { driveId } = getState();
      const endpoint = `${BASE_ORG_API}/${orgId}/recordings/${driveId}/publication`;

      try {
        await fetchWithAuthV2(endpoint, {
          method: 'PATCH',
          fallbackErrorMessage: `Failed to update recording's sample status`,
          body: JSON.stringify({ is_sample: sampleStatus }),
        });
        actions.setIsSample(sampleStatus);
      } catch (err) {
        Sentry.captureException(err, {
          tags: { section: 'DriveStateModel', thunk: 'updateSampleStatus' },
          extra: { orgId, driveId, sampleStatus },
        });
        throw err;
      }
    }
  ),

  updateDrivePublicTitle: thunk(
    async (actions, { title, orgId }, { getState }) => {
      const { driveId, isPublished } = getState();
      if (!isPublished) {
        console.warn(
          'Can only update public title of data already published. Will not update.'
        );
        return;
      }
      const fetchOptions = {
        method: 'PATCH',
        body: JSON.stringify({ title }),
      };
      const endpoint = `${BASE_ORG_API}/${orgId}/recordings/${driveId}/publication`;

      await fetchWithStatus(
        actions.setPublicationLoadStatus,
        // eslint-disable-next-line @typescript-eslint/require-await
        async (raw: RawPublicationResponse) => {
          const publicationInfo = transformRawPublicationResponse(raw);
          actions.setPublicationInfo(publicationInfo);
          actions.setIsPublished(true);
        },
        endpoint,
        fetchOptions
      );
    }
  ),

  updateRecordingTitle: thunk(async (actions, { title, orgId, driveId }) => {
    const endpoint = `${BASE_ORG_API}/${orgId}/recordings/${driveId}/recording_title`;

    try {
      await fetchWithAuthV2(endpoint, {
        method: 'PATCH',
        fallbackErrorMessage: 'Failed to update recording title',
        body: JSON.stringify({ recording_title: title }),
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          section: 'DriveStateModel',
          thunk: 'updateRecordingTitle',
        },
        extra: { title, orgId, driveId },
      });
      throw err;
    }
  }),

  unpublishDrive: thunk(async (actions, orgId, { getState }) => {
    const { driveId, isPublished } = getState();
    if (!isPublished) {
      console.warn('Drive is already private. Will not unpublish');
      return;
    }
    const endpoint = `${BASE_ORG_API}/${orgId}/recordings/${driveId}/publication`;
    try {
      await fetchWithAuthV2(endpoint, {
        method: 'DELETE',
        fallbackErrorMessage: 'Failed to unpublish the recording',
      });
      actions.setIsPublished(false);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'DriveStateModel', thunk: 'unpublishDriveById' },
        extra: { orgId, driveId },
      });
      throw err;
    }
  }),

  unpublishDriveById: thunk(async (_, { orgId, driveId }) => {
    const endpoint = `${BASE_ORG_API}/${orgId}/recordings/${driveId}/publication`;
    try {
      await fetchWithAuthV2(endpoint, {
        method: 'DELETE',
        fallbackErrorMessage: 'Failed to unpublish the recording',
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'DriveStateModel', thunk: 'unpublishDrive' },
        extra: { orgId, driveId },
      });
      throw err;
    }
  }),

  setPublicationInfo: action((state, info) => {
    state.publicationInfo = info;
  }),

  setIsPublished: action((state, isPublished) => {
    state.isPublished = isPublished;
  }),

  setIsSample: action((state, isSample) => {
    state.isSample = isSample;
  }),

  setIsShared: action((state, isShared) => {
    state.isShared = isShared;
  }),

  setPublicationLoadStatus: action((state, status) => {
    state.publicationLoadStatus = status;
  }),

  /// ///////////////////////////////////////////////////////////////////////////
  // Fetch source files
  /// ///////////////////////////////////////////////////////////////////////////

  loadSourceFileInfo: thunk(async (actions, { id, isPrivate, orgId }) => {
    // if drive is private, the id will be a number and if not, a string.
    if (isPrivate && Number.isNaN(parseInt(id, 10))) {
      throw new Error(
        `You can only load a private drive with an integer id. Instead received id ${id}`
      );
    }
    let SOURCE_FILE_INFO_REQ = '';
    if (isPrivate && parseInt(id, 10) && orgId) {
      SOURCE_FILE_INFO_REQ = `${BASE_ORG_API}/${orgId}/recordings/${id}/input/files/`;
    } else {
      // Published drive
      SOURCE_FILE_INFO_REQ = `${BASE_PUBLISHED_RECORDING_API}/${id}/input/files/`;
    }

    const fetchOptions = {
      method: 'GET',
    };
    try {
      const resp = await fetchWithAuth(SOURCE_FILE_INFO_REQ, fetchOptions);
      if (!resp.ok) {
        throw new Error(
          'Something went wrong when loading drive source file information'
        );
      }
      const filesJson = (await resp.json()) as FileInfo[];
      actions.setSourceFileInfo(filesJson);
    } catch (e) {
      Sentry.captureException(e, {
        tags: {
          section: 'DriveStateModel',
          thunk: 'loadSourceFileInfo',
        },
        extra: { id, isPrivate, orgId },
      });
    }
  }),

  loadOutputFileInfo: thunk(async (actions, { id, isPrivate, orgId }) => {
    if (isPrivate && Number.isNaN(parseInt(id, 10))) {
      throw new Error(
        `You can only load a private drive with an integer id. Instead received id ${id}`
      );
    }
    let url = '';
    if (isPrivate && parseInt(id, 10) && orgId) {
      url = `${BASE_ORG_API}/${orgId}/recordings/${id}/output/files/`;
    } else {
      // Published drive
      url = `${BASE_PUBLISHED_RECORDING_API}/${id}/output/files/`;
    }
    try {
      const resp = await fetchWithAuth(url, {
        method: 'GET',
      });
      if (!resp.ok) {
        throw new Error('Bad Response');
      }
      const filesJson = (await resp.json()) as OutputFileInfo[];
      actions.setOutputFileInfo(filesJson);
      actions.markDownloadFilesReady();
    } catch (e) {
      Sentry.captureException(e, {
        tags: {
          section: 'DriveStateModel',
          thunk: 'loadOutputFileInfo',
        },
        extra: { id, isPrivate, orgId },
      });
    }
  }),

  markDownloadFilesReady: action((state) => {
    state.filesToDownloadReady = true;
  }),

  // COMPUTED
  // ========

  /**
   * SLAM output files to download.
   * Filter out only 'slam' output type files. We pass all files marked as slam output through for download.
   * If SLAM is successful, we expect one each of:
   * - .zip (PLY file zipped)
   * - .osf (same filename is input file but postfixed with '_slam')
   * - .png
   * - .mp4 (Removed as of 05/09/24, but may be brought back in the future)
   */
  outputForDownload: computed((state) =>
    state.outputFileInfo.filter((file: OutputFileInfo) => {
      if (file.type === 'zip') {
        // PLY output files are zipped, but we want to show that they are PLY in the UI so
        // we augment it here with the intended display name.
        file.display_name = 'ply';
      }
      return file.output_type === 'slam';
    })
  ),
  outputForDownloadNonSlam: computed((state) =>
    state.outputFileInfo.filter(
      (file: OutputFileInfo) =>
        file.output_type === 'osf' && file.type === 'osf'
    )
  ),

  setNumFrames: action((state, numFrames: number) => {
    state.numFrames = numFrames;
  }),

  // Here we process the source files for download
  sourceDataForDownload: computed((state) =>
    state.sourceFileInfo.filter((file: FileInfo) => file.type === 'data')
  ),
  sourceConfigForDownload: computed((state) => {
    /* 
      For single-sensor, there is only one input config file which is json.
      For multi-sensor, user will upload tar file and config json is extracted from tar file during the runtime.
      As agreement, when .tar file existed in input files, we will let user to download the .tar file which is what they uploaded. And if there is no .tar file, the config type file will be returned as what single sensor does
    */
    const tarFile = state.sourceFileInfo.find((file: FileInfo) =>
      file.filename.endsWith('.tar')
    );
    if (tarFile) return tarFile;
    return state.sourceFileInfo.find(
      (file: FileInfo) => file.type === 'config'
    );
  }),

  // Backend processes that generate output
  outputProcesses: computed((state) => {
    const raw = state.driveInfo;
    if (!raw) return [];

    // In the future we will have this extracted to a separate resource as a list
    // Now we do not so we collect it to the list here so we are future compatible

    // Not all drives do have SLAM generated
    // If it is different than NOT_STARTED it should be there
    const outputs = [
      {
        name: 'osf',
        bucket: raw.osfBucket,
        prefix: raw.osfPrefix,
        generationDate: raw.osfGenerationDate,
        generationStatus: raw.osfGenerationStatus ?? 'UNKNOWN',
      } as Output,
    ];
    if (raw.slamGenerationStatus) {
      outputs.push({
        name: 'slam',
        bucket: raw.slamBucket,
        prefix: raw.slamPrefix,
        generationDate: raw.slamGenerationDate,
        generationStatus: raw.slamGenerationStatus ?? 'UNKNOWN',
      } as Output);
    }
    return outputs;
  }),
};
